<div class="ref-picker">
  @if (tab === 'library') {
    @if (showLibraryEmpty) {
      <div class="ref-picker__body">
        <div class="ref-picker__lib-empty">
          <div class="ref-picker__lib-empty-icon" aria-hidden="true">
            <i class="ph ph-book"></i>
          </div>
          <p class="ref-picker__lib-empty-title">{{ 'tenders.responses.documents.picker.lib-empty-title' | translate }}</p>
          <p class="ref-picker__lib-empty-subtitle">{{ 'tenders.responses.documents.picker.lib-empty-subtitle' | translate }}</p>
          <ex-button type="secondary" size="large" (click)="goToLibrary()">
            {{ 'tenders.responses.documents.picker.open-library' | translate }}
          </ex-button>
        </div>
      </div>
    } @else {
      <div class="ref-picker__body ref-picker__body--scroll-library">
        @if (showLibraryChrome) {
          <!-- Keep filters mounted across searches so focus / input state is preserved. -->
          <div class="ref-picker__chrome">
            <app-document-type-filter-bar
              [subtypes]="filterSubtypes"
              [activeIds]="activeSubtypeIds"
              [counts]="subtypeCounts"
              [selectedCounts]="selectedCounts"
              [wrapAll]="true"
              size="compact"
              (toggle)="onToggleSubtype($event)"
              (clear)="onClearSubtypes()"
            />
            <div class="ref-picker__toolbar">
              <button type="button" class="ref-picker__link-btn" (click)="selectAllVisible()">
                {{
                  (allVisibleSelected ? 'tenders.responses.documents.picker.deselect-all' : 'tenders.responses.documents.picker.select-all')
                    | translate
                }}
              </button>
              <span class="ref-picker__toolbar-spacer"></span>
              <ex-filter-text
                class="ref-picker__search"
                [placeholder]="'tenders.responses.documents.picker.search' | translate"
                [text]="query"
                [debounceMs]="250"
                variant="light"
                size="compact"
                (textEnter)="onSearch($event)"
              />
              <ex-sort-dropdown
                [items]="sortItems"
                [defaultValue]="sortDefault"
                [useShortLabel]="true"
                compact
                [shadowBorder]="false"
                (sort)="onSort($event)"
              />
            </div>
          </div>
        }

        @if (loading && documents.length === 0) {
          <div class="ref-picker__loading">
            <span class="ref-picker__load-more-spinner" aria-hidden="true"></span>
            <span>{{ 'tenders.responses.documents.picker.loading' | translate }}</span>
          </div>
        } @else {
          <div class="ref-picker__list" [class.ref-picker__list--refreshing]="loading">
            @for (doc of documents; track doc.id) {
              <button type="button" class="ref-picker__row" [class.ref-picker__row--selected]="isSelected(doc.id)" (click)="toggle(doc)">
                <span class="ref-picker__checkbox" [class.ref-picker__checkbox--on]="isSelected(doc.id)" aria-hidden="true">
                  @if (isSelected(doc.id)) {
                    <i class="ph ph-check"></i>
                  }
                </span>
                <app-document-identity
                  class="ref-picker__identity"
                  [name]="doc.name"
                  [subtypeName]="subtypeName(doc)"
                  [showUncategorized]="false"
                  [usedCount]="doc.projects?.length ?? 0"
                  [uploaderName]="doc.uploader?.name"
                  [updatedAt]="doc.updatedAt"
                  metaVariant="library"
                />
                @if (subtypeLabel(doc)) {
                  <span class="ref-picker__type-pill" [style.--pill-accent]="colorFor(doc)">{{ subtypeLabel(doc) }}</span>
                }
              </button>
            } @empty {
              @if (!loading) {
                <p class="ref-picker__no-results">{{ 'tenders.library.no-results' | translate }}</p>
              }
            }
            @if (hasMore) {
              <button type="button" class="ref-picker__load-more" [disabled]="loadingMore" (click)="loadMore()">
                @if (loadingMore) {
                  <span class="ref-picker__load-more-spinner" aria-hidden="true"></span>
                }
                {{ 'tenders.responses.documents.picker.load-more' | translate }}
              </button>
            }
          </div>
        }
      </div>
    }
  } @else {
    <div class="ref-picker__body" [class.ref-picker__body--scroll-import]="hasImportItems">
      @if (!hasImportItems) {
        <app-document-import-zone
          variant="dropzone"
          [mainTitle]="'tenders.responses.documents.empty-main-title-internal' | translate"
          [subtitle]="'tenders.responses.documents.recommended-internal' | translate"
          [hints]="importHints"
          [footnote]="importFootnote"
          (filesSelected)="onFilesSelected($event)"
          (filesRejected)="onFilesRejected($event)"
        />
      } @else {
        @if (rejected.length) {
          <div class="ref-picker__rejected">
            @for (entry of rejected; track entry.name) {
              <div class="ref-picker__rejected-row">
                <span>{{ entry.name }}</span>
                <span>— {{ entry.reason | translate }}</span>
              </div>
            }
          </div>
        }
        @if (duplicates.length) {
          <div class="ref-picker__dedup">
            <app-duplicate-resolver surface="project-picker" [items]="duplicates" (resolve)="onDuplicateResolve($event)" />
          </div>
        }
        <div class="ref-picker__staged">
          @for (item of staged; track stagedTrackId(item)) {
            <div class="ref-picker__staged-row">
              @if (item.kind === 'library') {
                <app-document-identity
                  class="ref-picker__staged-identity"
                  [name]="item.name"
                  [subtypeName]="item.subtypeName"
                  [showUncategorized]="false"
                  [usedCount]="item.usedCount"
                  [uploaderName]="item.ownerName"
                  [inLibraryLabel]="'tenders.responses.documents.in-library' | translate"
                />
              } @else {
                <app-document-identity
                  class="ref-picker__staged-identity"
                  [name]="item.name"
                  [fileSize]="item.file.size"
                  [showMeta]="true"
                  [showUncategorized]="false"
                />
              }
              <button
                type="button"
                class="ref-picker__remove"
                (click)="removeStaged(item)"
                [attr.aria-label]="'tenders.responses.documents.delete-file' | translate"
              >
                <i class="ph ph-x" aria-hidden="true"></i>
              </button>
            </div>
          }
        </div>
        <div class="ref-picker__import-more">
          <app-document-import-zone
            variant="compact"
            [addLabel]="'tenders.responses.documents.picker.import-more' | translate"
            (filesSelected)="onFilesSelected($event)"
            (filesRejected)="onFilesRejected($event)"
          />
        </div>
      }
    </div>
  }

  <div class="ref-picker__footer">
    <div class="ref-picker__quota" [class.ref-picker__quota--over]="isOverQuota" [class.ref-picker__quota--empty]="totalPreview === 0">
      <div class="ref-picker__quota-bar">
        <div class="ref-picker__quota-fill" [style.width.%]="(totalPreview / maxDocuments) * 100"></div>
      </div>
      <span class="ref-picker__quota-label">{{ totalPreview }} / {{ maxDocuments }}</span>
    </div>
    <span class="ref-picker__footer-spacer"></span>
    <ex-button type="primary" icon="arrow-right" iconPosition="right" size="large" [disabled]="!canConfirm" (click)="onConfirm()">
      {{ confirmLabelKey | translate }}
      @if (removedCount === 0 && addedCount > 0) {
        ({{ addedCount }})
      }
    </ex-button>
  </div>
</div>
